import { useCallback } from 'react';
import MainPageLayout from 'layouts/MainPageLayout';
import { FormattedMessage } from 'react-intl';
import { Grid, Typography, Link } from '@mui/material';
import { FilledButton, Image } from 'components';
import { TEAMVIEWER_DOWNLOAD_URL } from 'constants/externalURLs';
import useStyles from './Teamviewer.styles';
import askTeamviewerTablet from 'assets/images/tablets/ask_teamviewer_tablet.png';
import { useMutation } from '@apollo/client';
import {
  SEND_GCM_BY_CHANNEL_MUTATION,
  SendGCMByChannelMutationOutput,
} from 'graphql/gcm/GCMMutations';
import { useSelectedLinkContext } from 'contexts';
import { trackEvent, GACommonEventAction, GAEventCategory} from 'services/googleAnalytics';
import {useSnackbar} from 'hooks';

type TeamviewerPureProps = {
  handleLaunchTeamviewerClick: Function;
};

export const TeamviewerPure = ({
  handleLaunchTeamviewerClick,
}: TeamviewerPureProps): JSX.Element => {
  const classes = useStyles();
  return (
    <MainPageLayout title={<FormattedMessage id="teamviewer.description" />}>
      <Grid alignItems="center" className={classes.firstGrid} container>
        <Grid item>
          <Typography className={classes.p} variant="body1">
            <FormattedMessage id="teamviewer.p1" />
          </Typography>

          <Typography className={classes.p} variant="body1">
            <FormattedMessage id="teamviewer.p2" />
            <Link href={TEAMVIEWER_DOWNLOAD_URL} target="_blank">
              {TEAMVIEWER_DOWNLOAD_URL}
            </Link>
            .
          </Typography>

          <Typography className={classes.p} variant="body1">
            <FormattedMessage id="teamviewer.p3" />
          </Typography>
        </Grid>
      </Grid>

      <Grid className={classes.teamviewerButtonContainer} container>
        <Grid item>
          <FilledButton
            className={classes.button}
            color="violet"
            data-testid="teamviewer-button"
            onClick={(): void => handleLaunchTeamviewerClick()}
          >
            <FormattedMessage id="teamviewer.button" />
          </FilledButton>
        </Grid>
      </Grid>

      <Grid alignItems="center" className={classes.secondGrid} container>
        <Grid item md={7}>
          <Typography className={classes.p} variant="body1">
            <FormattedMessage id="teamviewer.p4" />
            <span className={classes.launchApp}>
              <FormattedMessage id="teamviewer.p4.launchApp" />
            </span>
            .
          </Typography>

          <Typography className={classes.p} variant="body1">
            <FormattedMessage id="teamviewer.p5" />
          </Typography>

          <Typography className={classes.p} variant="body1">
            <FormattedMessage id="teamviewer.p6" />
          </Typography>

          <Typography className={classes.p} variant="body1">
            <FormattedMessage id="teamviewer.p7" />
          </Typography>

          <Typography className={classes.p} variant="body1">
            <FormattedMessage id="teamviewer.p8" />
            <Link href="/pdf/help/Guide détaillé - Teamviewer.pdf" target="_blank">
              <FormattedMessage id="teamviewer.p8.link" />
            </Link>
            .
          </Typography>
        </Grid>

        <Grid item md={5}>
          <Image alt="Teamviewer" src={askTeamviewerTablet} width="100%" />
        </Grid>
      </Grid>
    </MainPageLayout>
  );
};

const Teamviewer = (): JSX.Element | null => {
  const [sendGCMByChannel] = useMutation<SendGCMByChannelMutationOutput>(
    SEND_GCM_BY_CHANNEL_MUTATION,
  );
  const { selectedLink } = useSelectedLinkContext();
  const { errorSnackbar, successSnackbar } = useSnackbar();
  const channelKey = selectedLink?.channelKey;

  const handleLaunchTeamviewerClick = useCallback(() => {
    trackEvent(GAEventCategory.HELP, GACommonEventAction.CLICK, 'Teamviewer');

    sendGCMByChannel({
      variables: {
        channel: channelKey,
        gcmOrderType: 'TEAMVIEWER',
      },
    })
      .then(({ data }) => {
        if (data?.sendGCMByChannel?.sent) {
          successSnackbar(
            <FormattedMessage id="teamviewer.successMessage" />,
          );
        } else {
          errorSnackbar(<FormattedMessage id="teamviewer.errorMessage" />);
        }
      })
      .catch(() =>
        errorSnackbar(<FormattedMessage id="teamviewer.errorMessage" />),
      );
  }, [channelKey, sendGCMByChannel, errorSnackbar, successSnackbar]);

  if (!selectedLink) {
    return null;
  }

  return (
    <TeamviewerPure handleLaunchTeamviewerClick={handleLaunchTeamviewerClick} />
  );
};

export default Teamviewer;
